import { produce } from 'immer';
import { arrayUnique } from 'lib/riskbase';
import * as actions from 'store/actions';
import { AuthenticationSteps, AuthState } from 'store/auth/types';
import { createReducer } from 'typesafe-actions';

const initialState: AuthState = {
  promptAuth: false,
  // Signing in data
  step: AuthenticationSteps.email,
  authStepEmail: undefined,
  authStepCanAs: undefined,
  authStepAs: undefined,
  authStepToken: undefined,
  authStepTfaSetup: undefined,
  error: undefined,
  // Signed in data
  authorization_token: undefined,
  urlToken: undefined,
  user: undefined,
  organisations: {},
  organisation: null,
};

export const authReducer = createReducer(initialState)
  .handleAction(actions.SIGN_OUT.success, () => initialState)
  .handleAction(actions.doAuthShowAuthModal, (state) =>
    produce(state, (draft) => {
      draft.error = undefined;
      draft.promptAuth = true;
      draft.step = AuthenticationSteps.email;
      draft.authStepAs = undefined;
      draft.authStepCanAs = undefined;
      draft.authStepToken = undefined;
      draft.authStepEmail = undefined;
    }),
  )
  .handleAction(actions.UPDATE_USER, (state, action) => {
    return { ...state, user: action.payload };
  })
  .handleAction(actions.doUserSetUrlToken, (state, action) => {
    return { ...state, urlToken: action.payload };
  })
  .handleAction(actions.UPDATE_USER_NAME, (state, action) =>
    produce(state, (draft) => {
      if (draft.user) {
        draft.user.name = action.payload;
      }
      return draft;
    }),
  )
  .handleAction(actions.UPDATE_USER_AVATAR, (state, action) =>
    produce(state, (draft) => {
      if (draft.user) {
        draft.user.avatar = action.payload || '';
      }
      return draft;
    }),
  )
  .handleAction(actions.UPDATE_USER_EMAIL, (state, action) =>
    produce(state, (draft) => {
      if (draft.user) {
        draft.user.email = action.payload;
      }
      return draft;
    }),
  )
  .handleAction(actions.UPDATE_USER_OFA, (state, action) =>
    produce(state, (draft) => {
      if (draft.user) {
        draft.user.ofa = action.payload;
      }
      return draft;
    }),
  )
  .handleAction(actions.RESET_AUTH_STEP, (state) => {
    return {
      ...state,
      step: AuthenticationSteps.email,
      error: undefined,
      authStepAs: undefined,
      authStepCanAs: undefined,
      authStepEmail: undefined,
      authStepToken: undefined,
      user: state.authorization_token ? state.user : undefined,
    };
  })
  .handleAction(actions.AUTHENTICATE.request, (state) => {
    return { ...state, error: undefined };
  })
  .handleAction(
    [actions.AUTHENTICATE.success, actions.REQUEST_PASSWORD_RESET.success],
    (state, action) => {
      const { step, data } = action.payload;
      if (step === AuthenticationSteps.email) {
        return {
          ...state,
          error: undefined,
          step,
          user: state.authorization_token ? state.user : undefined,
        };
      }
      if (
        step === AuthenticationSteps.emailvalidate ||
        step === AuthenticationSteps.password
      ) {
        return {
          ...state,
          step,
          error: undefined,
          authStepToken: data.token,
          authStepEmail: data.email,
          authStepCanAs: data.as,
          user: state.user ? { ...state.user, email: data.email } : state.user,
        };
      }
      if (step === AuthenticationSteps.tfaSetup) {
        return {
          ...state,
          step,
          error: undefined,
          authStepToken: data.token,
          authStepEmail: data.email,
          authStepTfaSetup: data.data,
          user: state.user ? { ...state.user, email: data.email } : state.user,
        };
      }
      if (
        step === AuthenticationSteps.tfa ||
        step === AuthenticationSteps.setpassword
      ) {
        return {
          ...state,
          step,
          error: undefined,
          authStepToken: data.token,
          authStepEmail: undefined,
          user: data.user,
        };
      }
      if (step === AuthenticationSteps.authenticated) {
        return {
          ...state,
          step,
          error: undefined,
          authStepToken: undefined,
          authStepEmail: undefined,
          authorization_token: data.authToken,
          urlToken: data.urlToken,
          user: data.user,
        };
      }
      return state;
    },
  )
  .handleAction(
    [actions.AUTHENTICATE.failure, actions.REQUEST_PASSWORD_RESET.failure],
    (state, action) => {
      if (
        action.payload.message === 'ClientOutOfDateMinor' ||
        action.payload.message === 'ClientOutOfDateMajor'
      ) {
        return { ...state, error: action.payload.message };
      }
      return state;
    },
  )
  .handleAction(actions.doAuthHideAuthModal, (state) => {
    return {
      ...state,
      step: AuthenticationSteps.email,
      error: undefined,
      promptAuth: false,
      authStepAs: undefined,
      authStepToken: undefined,
      authStepEmail: undefined,
      authStepTfaSetup: undefined,
    };
  })
  .handleAction(actions.UPDATE_USER_TFA, (state, action) =>
    produce(state, (draft) => {
      if (draft.user) {
        draft.user.tfa = action.payload;
      }
      return draft;
    }),
  )
  .handleAction(actions.doUserOrganisationSet, (state, action) => ({
    ...state,
    organisation: action.payload.organisation,
  }))
  .handleAction(actions.UPDATE_USER_ORGANISATIONS, (state, action) =>
    produce(state, (draft) => {
      const newOrganisations = action.payload;
      const allOrganisationIds = arrayUnique([
        ...Object.keys(state.organisations),
        ...Object.keys(newOrganisations),
      ]);
      allOrganisationIds.forEach((organisationId) => {
        if (!newOrganisations[organisationId]) {
          delete draft.organisations[organisationId];
        } else {
          draft.organisations[organisationId] = {
            ...(draft.organisations[organisationId] || {
              details: {
                name: 'Unknown',
                pngLogo: null,
                svgLogo: null,
                address1: '',
                address2: '',
                city: '',
                county: '',
                postcode: '',
                phone: '',
              },
            }),
            ...newOrganisations[organisationId]!,
          };
        }
      });
      return draft;
    }),
  )
  .handleAction(actions.doAuthSetSignInAs, (state, action) => ({
    ...state,
    authStepAs: action.payload,
  }));
